import React from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast, Toaster } from "sonner"; // Import Toaster for toast notifications
import { useCreateUser } from "@/hooks/mutations/users";
import { authService } from "@/services";
import logo from "@/assets/images/logo.png";
import { useLegaladviser } from "@/hooks/stores/useLegaladviser";

export const SignupLegaltoUser = () => {
  const navigate = useNavigate();
  const { setUserId } = useLegaladviser();

  const createUserMutation = useCreateUser({
    onSuccess: (response) => {
      console.log("API Response:", response);

      if (!response || !response.user) {
        toast.error("Failed to retrieve user information.");
        return;
      }

      const { user } = response;
      console.log("User role:", user.roles[0].title);

      // Set the user role since it's required for routing
      setUserId(user?.id);

      toast.success(response.message || "Account created successfully!");

      // This is the navigation logic
      if (user.roles[0].title === "User") {
        navigate("/video");
      } else {
        navigate("/");
      }
    },
    onError: (error) => {
      console.error("Error during signup:", error);

      if (!error.response?.data) {
        toast.error(error.message || "Signup failed. Please try again.");
        return;
      }

      const errorData = error.response.data;

      // Handle validation errors including "email already taken"
      if (error.response.status === 422) {
        if (errorData.errors && typeof errorData.errors === "object") {
          const message = Object.values(errorData.errors).flat().join(", ");
          toast.error(message);
        } else {
          toast.error(errorData.message || "Validation failed. Please try again.");
        }
        return;
      }

      // Handle any other error messages
      toast.error(errorData.message || "Signup failed. Please try again.");
    },
  });

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
    password_confirmation: Yup.string()
      .oneOf([Yup.ref("password")], "Passwords must match")
      .required("Confirm Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
      password_confirmation: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        // First logout any existing user
        // await authService.logout();

        // Then proceed with user creation
        const userData = {
          name: values.name.trim(),
          email: values.email.trim(),
          password: values.password.trim(),
          roles: [2], // Default User role ID
        };
        console.log("User Data:", userData);
        createUserMutation.mutate(userData);
      } catch (error) {
        console.error("Error during signup process:", error);
      }
    },
  });

  return (
    <div
      className="fixed inset-0 w-full h-full flex flex-col items-center justify-center"
      style={{ backgroundColor: "#F1EBDB" }}
    >
      <div className="w-full max-w-md mx-4 space-y-8 bg-white p-10 rounded-xl shadow-lg">
        <div>
          <h2 className="text-3xl font-bold text-center text-black">
            PostScrypt
          </h2>
          <p className="mt-2 text-sm text-[#F16745] text-center mb-8">
            Fill in your details below to start your journey
          </p>
        </div>

        <form onSubmit={formik.handleSubmit} className="space-y-6">
          <div className="rounded-md">
            <div className="mb-4">
              <label htmlFor="name" className="block font-medium">
                Name
              </label>
              <input
                id="name"
                type="text"
                name="name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
                className="mt-1 appearance-none text-black relative block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F16745] focus:border-[#F16745] focus:z-10 sm:text-sm"
                placeholder="Enter your name"
              />
              {formik.touched.name && formik.errors.name && (
                <p className="mt-2 text-sm text-destructive">
                  {formik.errors.name}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="email" className="block font-medium">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                name="email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                className="mt-1 appearance-none text-black relative block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F16745] focus:border-[#F16745] focus:z-10 sm:text-sm"
                placeholder="Enter your email"
              />
              {formik.touched.email && formik.errors.email && (
                <p className="mt-2 text-sm text-destructive">
                  {formik.errors.email}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="password" className="block font-medium">
                Password
              </label>
              <input
                id="password"
                type="password"
                name="password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password}
                className="mt-1 appearance-none text-black relative block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F16745] focus:border-[#F16745] focus:z-10 sm:text-sm"
                placeholder="Enter your password"
              />
              {formik.touched.password && formik.errors.password && (
                <p className="mt-2 text-sm text-destructive">
                  {formik.errors.password}
                </p>
              )}
            </div>

            <div className="mb-4">
              <label
                htmlFor="password_confirmation"
                className="block font-medium"
              >
                Confirm Password
              </label>
              <input
                id="password_confirmation"
                type="password"
                name="password_confirmation"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password_confirmation}
                className="mt-1 appearance-none text-black relative block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F16745] focus:border-[#F16745] focus:z-10 sm:text-sm"
                placeholder="Confirm your password"
              />
              {formik.touched.password_confirmation &&
                formik.errors.password_confirmation && (
                  <p className="mt-2 text-sm text-destructive">
                    {formik.errors.password_confirmation}
                  </p>
                )}
            </div>
          </div>

          <button
            type="submit"
            disabled={createUserMutation.isPending || !formik.isValid}
            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#F16745] hover:bg-[#F16745]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F16745] disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-150 hover:scale-[1.02]"
          >
            {createUserMutation.isPending ? (
              <span className="flex items-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Creating Account...
              </span>
            ) : (
              "Create Account"
            )}
          </button>
        </form>
      </div>
      <div className="mt-8 mb-1">
        <img src={logo} alt="PostScrypt Logo" className="h-16 w-auto" />
      </div>
    </div>
  );
};